import { useState } from 'react';

import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider';
import { browserLogger } from '@widget/utils/datadog';
import { useLeadData } from '@widget/core';
import { updateLeadData } from '@widget/api';
import {
  FormWrap,
  StyledButton,
  StyledSelect,
  Title,
  Wrap,
} from '@widget/components/FeedbackForm/styles';

import type { FeedbackFormSchema } from '@widget/storyblok/schemas';
import type { z } from 'zod';

const DEFAULT_OTHER_VALUE = 'other';
const DEFAULT_OTHER_LABEL = 'Other';
const DEFAULT_INPUT_LABEL = 'Search Engine (Google, Bing, etc.)';

type FeedbackFormProps = z.infer<typeof FeedbackFormSchema>;
export const FeedbackForm = ({
  title,
  submitText,
  successText,
  fields,
  otherFieldValue,
  otherFieldLabel,
  inputLabel,
}: FeedbackFormProps) => {
  const { leadData } = useLeadData();
  const [value, setValue] = useState('');
  const [success, setSuccess] = useState(false);

  const options = [
    ...(fields || []),
    { key: otherFieldLabel || DEFAULT_OTHER_LABEL, value: otherFieldValue || DEFAULT_OTHER_VALUE },
  ].map((o) => ({
    value: o.value,
    label: o.key,
  }));

  async function onSubmit() {
    try {
      if (!leadData?.id) {
        return;
      }

      setSuccess(true);

      await updateLeadData(leadData.id, { howDidYouHearAboutUs: value });
    } catch (error) {
      browserLogger.error(error, 'error in feedback form');
    }
  }

  return (
    <ThemeProvider>
      <Wrap>
        <Title variant="h3">{success ? successText : title}</Title>
        {!success && (
          <>
            <FormWrap>
              <StyledSelect
                label={inputLabel || DEFAULT_INPUT_LABEL}
                value={value}
                options={options}
                onChange={(event) => {
                  setValue(event.target.value as string);
                }}
              />
              <StyledButton
                onClick={onSubmit}
                disabled={!value}
                variant={'contained'}
                color={'secondary'}
                disableElevation
              >
                {submitText}
              </StyledButton>
            </FormWrap>
          </>
        )}
      </Wrap>
    </ThemeProvider>
  );
};
